import { sizeKeyForPlayers } from "core/src/core/arena-sizes.js";
import * as scoreUtil from "core/src/core/score/score-util.js";
import { getPlaystyle } from "core/src/playstyles.js";
import { ALL_POWERUP_KEYS, DEFAULT_SPAWN_CHANCE, DEFAULT_WORM_SPEED, SPAWN_RATES, WORM_SPEEDS } from "../../shared/powerups";
import { modeLook } from "../../online/dynamic";
import { MAP_SIZES, SHAPE_NAMES, mapDescriptor, mapValueFor, mapValueOf, shapeAndSizeOf, type MapDesc, type MapShape, type MapSizeKey } from "./maps";

// The rules of a Game Room (and of a local game), the easy way: a handful of
// plain choices with smart defaults. The map size and the score target follow
// the number of players unless the host overrides them under "More options".
// What the server (or the local match) gets is the same concrete config as
// before: configOf() turns these choices into it, and describeConfig() reads a
// config back into the one plain rules line, so host and guests agree on it.

export type ModeId = "classic" | "pure" | "chaos" | "identity" | "drink" | "fatboy" | "custom";
// "Quick duel" is Classic for two on a small map, first to 3: a flavour, not a mode.
export type FlavourId = ModeId | "duel";
export type Shape = MapShape | "surprise";
export type Size = MapSizeKey | "auto";
export type Len = "short" | "normal" | "long";
export type Tri = 0 | 1 | 2;
export type Spawn = 0 | 1 | 2 | 3 | 4;

export interface Rules {
  base: Exclude<ModeId, "custom">; // the last named mode: what "Back to ..." returns to
  players: number;                 // seats in the room
  shape: Shape;
  rolled: "stadium" | "rectangle"; // what "Surprise me" drew last
  size: Size;
  length: Len;
  exact: number | null;            // an exact score instead of Short / Normal / Long
  win: "score" | "rounds";
  rounds: number;
  pu: string[];                    // the power-ups that are ON
  amount: Spawn;                   // how often power-ups spawn: Off, Rare, Normal, Frequent, Chaos
  amountV?: number;                // (5: amount is on that five-step scale; older saves had three)
  speed: Tri;                      // Slow / Normal / Fast
  fill: boolean;                   // fill empty seats when the match starts
  fillSkill: number;               // how good they are, 1 (easy) to 10 (hard)
  respawn: boolean;                // a crash puts you back in 2 s later and rounds run on a timer (core respawn.js); off: out until the next round
}

// How often power-ups spawn: the five rates the game has always had
// (shared/powerups SPAWN_RATES: Off, Rare, Normal, Frequent, Chaos).
export const AMOUNTS: { label: string; value: number }[] = SPAWN_RATES;
export const SPEEDS: { label: string; value: number }[] = WORM_SPEEDS;
// A rounds match has no score target (as the local setup always did it).
const NO_SCORE_LIMIT = 9999;

const enabledOf = (playstyle: string): string[] => {
  const e = getPlaystyle(playstyle)?.enabled;
  return !e || e === "all" ? ALL_POWERUP_KEYS.slice() : (e as string[]).slice();
};

export interface ModeInfo { id: ModeId; name: string; line: string; color: string; icon: string; pu: string[]; amount: Spawn }
// The named game modes: the power-ups that are on and how many of them spawn.
// Their one-liners are the round modes' own (online/dynamic.ts MODE_LOOK, the
// ones written for Play Now and the Royale), so a mode reads the same everywhere.
// The gameplay of each is core's playstyle of the same kind (core/src/playstyles.js).
export const MODES: ModeInfo[] = [
  { id: "classic", name: "Classic", line: "All power-ups", color: "#19f0b0", icon: "bolt", pu: enabledOf("Classic"), amount: 2 },
  { id: "pure", name: "No power-ups", line: "", color: "#7fd0ff", icon: "target", pu: [], amount: 2 },
  { id: "chaos", name: "Sabotage", line: modeLook("Sabotage").line, color: "#ff5c7a", icon: "flame", pu: enabledOf("Sabotage"), amount: 3 },
  { id: "identity", name: "Identity crisis", line: modeLook("Identity crisis").line, color: "#b06cff", icon: "users", pu: enabledOf("Identity crisis"), amount: 2 },
  { id: "drink", name: "Drink and drive", line: modeLook("Drink and drive").line, color: "#ff9f45", icon: "cup", pu: enabledOf("Drink and drive"), amount: 3 },
  { id: "fatboy", name: "Fatboy Slim", line: modeLook("Fatboy Slim").line, color: "#7CFC66", icon: "burger", pu: enabledOf("Fatboy Slim"), amount: 2 },
  { id: "custom", name: "Custom", line: "Your own mix", color: "#9fb2cc", icon: "sliders", pu: [], amount: 2 },
];
const DUEL = { id: "duel" as const, name: "Quick duel", line: "2 players, small map, first to 3.", color: "#a78bff", icon: "duel" };
export function flavourInfo(id: FlavourId): { id: FlavourId; name: string; line: string; color: string; icon: string } {
  return id === "duel" ? DUEL : MODES.find((m) => m.id === id) || MODES[0];
}
// The big cards a new room starts from, in order. (No "Party": a custom room
// plays one mode and one map for the whole match.)
export const FLAVOUR_CARDS: FlavourId[] = ["classic", "pure", "chaos", "duel", "custom"];

// ---------- smart defaults ----------
export const normalScore = (players: number): number => scoreUtil.targetScoreFor(players);
// A Respawn round pays points for crashes made and time alive, about 15 for the
// round's best in its 2 minutes, whatever the room's size: its lengths are its own.
const RESPAWN_SCORE: Record<Len, number> = { short: 25, normal: 50, long: 100 };
export function lenScore(players: number, len: Len, respawn = false): number {
  if (respawn) return RESPAWN_SCORE[len];
  const n = normalScore(players);
  return len === "short" ? Math.max(3, Math.round(n / 2)) : len === "long" ? n * 2 : n;
}
// a rounds match's usual lengths (a Respawn round is 2 minutes, a survival round under one)
export function roundLen(len: Len, respawn = false): number {
  return (respawn ? { short: 2, normal: 3, long: 5 } : { short: 5, normal: 10, long: 20 })[len];
}
export const scoreOf = (r: Rules): number => r.exact ?? lenScore(r.players, r.length, r.respawn);
export const autoSize = (players: number): MapSizeKey => sizeKeyForPlayers(players) as MapSizeKey;
export const sizeOf = (r: Rules): MapSizeKey => (r.size === "auto" ? autoSize(r.players) : r.size);
export const sizeName = (key: MapSizeKey): string => MAP_SIZES.find((z) => z.key === key)?.label || key;

export const START: Rules = {
  base: "classic", players: 8, shape: "stadium", rolled: "stadium", size: "auto", length: "normal", exact: null, win: "score", rounds: 10,
  pu: ALL_POWERUP_KEYS.slice(), amount: 2, amountV: 5, speed: 1, fill: true, fillSkill: 5, respawn: true,
};

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

// ---------- the concrete config ----------
export interface RoomConfig {
  map: MapDesc;
  maxPlayers: number;
  botFill: boolean;
  botFillSkill?: number;
  maxScore?: number;
  maxRounds?: number;
  disabledPowerUps: string[];
  powerUpSpawnChance: number;
  wormSpeed: number;
  respawn: boolean;
}

export const rollShape = (): "stadium" | "rectangle" => (Math.random() < 0.5 ? "stadium" : "rectangle");
const shapeOf = (r: Rules): MapShape => (r.shape === "surprise" ? r.rolled : r.shape);
export const mapValueOfRules = (r: Rules): string => mapValueFor(shapeOf(r), sizeOf(r));

export function configOf(r: Rules): RoomConfig {
  const none = r.pu.length === 0;
  return {
    map: mapDescriptor(mapValueOfRules(r)),
    maxPlayers: r.players,
    botFill: r.fill,
    botFillSkill: r.fill ? r.fillSkill : undefined,
    maxScore: r.win === "score" ? scoreOf(r) : NO_SCORE_LIMIT,
    maxRounds: r.win === "rounds" ? r.rounds : undefined,
    disabledPowerUps: ALL_POWERUP_KEYS.filter((k) => !r.pu.includes(k)),
    powerUpSpawnChance: none ? 0 : AMOUNTS[r.amount].value, // (Off is 0 too)
    wormSpeed: SPEEDS[r.speed].value,
    respawn: r.respawn,
  };
}

// ---------- reading a config back ----------
type AnyConfig = { map?: any; maxPlayers?: number | null; maxScore?: number | null; maxRounds?: number | null; disabledPowerUps?: string[] | null; powerUpSpawnChance?: number | null; wormSpeed?: number | null; respawn?: boolean | null };
const near = (a: number, b: number) => Math.abs(a - b) < 1e-6;

// The mode a config reads as: a named mode means exactly its power-ups, its
// amount and normal speed. Anything else is Custom.
export function modeOfConfig(c: AnyConfig): ModeId {
  const speed = typeof c.wormSpeed === "number" ? c.wormSpeed : DEFAULT_WORM_SPEED;
  const spawn = typeof c.powerUpSpawnChance === "number" ? c.powerUpSpawnChance : DEFAULT_SPAWN_CHANCE;
  const off = new Set(c.disabledPowerUps || []);
  const on = ALL_POWERUP_KEYS.filter((k) => !off.has(k));
  if (!near(speed, DEFAULT_WORM_SPEED)) return "custom";
  if (spawn === 0 || on.length === 0) return "pure";
  const m = MODES.find((x) => x.id !== "custom" && x.id !== "pure" && sameSet(on, x.pu) && near(spawn, AMOUNTS[x.amount].value));
  return m ? m.id : "custom";
}

export function flavourOfConfig(c: AnyConfig): FlavourId {
  const mode = modeOfConfig(c);
  const a = c.map ? shapeAndSizeOf(mapValueOf(c.map)) : null;
  if (mode === "classic" && !c.respawn && c.maxPlayers === 2 && !c.maxRounds && c.maxScore === lenScore(2, "short") && a?.size === "small") return "duel";
  return mode;
}
export const modeOf = (r: Rules): ModeId => modeOfConfig(configOf(r));
export const flavourOf = (r: Rules): FlavourId => flavourOfConfig(configOf(r));

// The one plain rules line: "Classic · Stadium · first to 20 · up to 8 players".
// `randomMap`: the host picked "Surprise me" (the shape is drawn at the start).
// `exactSeats`: a local game, where the number is who plays, not a cap.
export function describeConfig(c: AnyConfig, opts: { randomMap?: boolean; exactSeats?: boolean } = {}): string {
  const seats = c.maxPlayers || 2;
  const a = c.map ? shapeAndSizeOf(mapValueOf(c.map)) : null;
  const size = a && a.size !== autoSize(seats) ? sizeName(a.size) : "";
  const shape = opts.randomMap ? "Random map" : a ? SHAPE_NAMES[a.shape] : "";
  const map = opts.randomMap ? (size ? `Random map, ${size}` : "Random map") : `${size} ${shape}`.trim();
  const len = c.maxRounds ? `${c.maxRounds} rounds` : `first to ${typeof c.maxScore === "number" ? c.maxScore : lenScore(seats, "normal", !!c.respawn)}`;
  const who = opts.exactSeats || seats === 2 ? `${seats} players` : `up to ${seats} players`;
  return [flavourInfo(flavourOfConfig(c)).name, c.respawn ? "Respawn" : "", map, len, who].filter(Boolean).join(" · ");
}
export const describeRules = (r: Rules, local = false): string => describeConfig(configOf(r), { randomMap: r.shape === "surprise", exactSeats: local });

// ---------- changing the rules ----------
// Picking a flavour sets the mode and clears every advanced override.
export function applyFlavour(r: Rules, f: FlavourId, local = false): Rules {
  if (f === "custom") return r; // Custom keeps what is set and opens the options
  const wasDuel = flavourOf(r) === "duel";
  const mode = MODES.find((m) => m.id === (f === "duel" ? "classic" : f)) || MODES[0];
  const clean: Rules = { ...r, base: mode.id as Rules["base"], exact: null, win: "score", size: "auto", speed: 1, amount: mode.amount, pu: mode.pu.slice() };
  // the duel is played to the last worm, rounds of survival
  if (f === "duel") return { ...clean, players: 2, length: "short", respawn: false };
  // Leaving the duel gives the room its seats, its normal length and Respawn back.
  if (!wasDuel) return clean;
  return local ? { ...clean, respawn: true } : { ...clean, players: START.players, length: "normal", respawn: true };
}

// What the power-up grid's quick sets are: every named mode's power-ups.
export const POWERUP_SETS: { name: string; pu: string[]; amount: Spawn }[] = [
  { name: "All", pu: MODES[0].pu, amount: 2 },
  { name: "None", pu: [], amount: 2 },
  ...MODES.filter((m) => !["classic", "pure", "custom"].includes(m.id)).map((m) => ({ name: m.id === "chaos" ? "Sabotage" : m.name, pu: m.pu, amount: m.amount })),
];
export const isSet = (r: Rules, pu: string[]) => sameSet(r.pu, pu);

// A saved or handed-over Rules object, made safe to use (old or partial data).
export function cleanRules(raw: any): Rules {
  const r = { ...START, ...(raw && typeof raw === "object" ? raw : {}) } as Rules;
  r.players = Math.max(2, Math.min(40, Math.floor(Number(r.players) || START.players)));
  r.pu = Array.isArray(r.pu) ? r.pu.filter((k) => ALL_POWERUP_KEYS.includes(k)) : START.pu.slice();
  // an older save counted Few / Normal / Lots (0-2): those are Rare / Normal / Frequent now
  if (r.amountV !== 5 && [0, 1, 2].includes(r.amount)) r.amount = (r.amount + 1) as Spawn;
  if (![0, 1, 2, 3, 4].includes(r.amount)) r.amount = 2;
  r.amountV = 5;
  if (![0, 1, 2].includes(r.speed)) r.speed = 1;
  r.fillSkill = Math.max(1, Math.min(10, Math.round(Number(r.fillSkill) || START.fillSkill)));
  if (!MODES.some((m) => m.id === r.base && (m.id as string) !== "custom")) r.base = "classic";
  return r;
}
